import { call, put, takeLatest, all } from 'redux-saga/effects';

import { heliumReceive, requestAndSet } from 'utils/helpers';

import { visitorsActions } from '../slices/visitorsSlice';
import { visitsActions } from '../slices/visitsSlice';

interface BlobResponse {
  data: Blob;
}

function* updateVisitorSaga(
  action: ReturnType<typeof visitorsActions.updateVisitor>,
): Generator {
  const { orgId, visitId, visitorId, visitorPayload } = action.payload;
  yield requestAndSet('updateVisitors', [orgId, visitId, visitorId], {
    body: visitorPayload,
    loadingAction: (v: boolean) =>
      visitorsActions.setLoading({ key: 'visitorLoading', value: v }),
    *postSuccessAction() {
      yield put(visitsActions.fetchVisits({ orgId }));
      yield put(visitsActions.describeVisit({ orgId, visitId }));
    },
  });
}

function* addVisitorsToVisitSaga(
  action: ReturnType<typeof visitorsActions.addVisitorsToVisit>,
): Generator {
  const { orgId, visitId, visitors } = action.payload;
  yield requestAndSet('addVisitors', [orgId, visitId], {
    body: { visitors },
    loadingAction: (v: boolean) =>
      visitorsActions.setLoading({ key: 'visitorLoading', value: v }),
    *postSuccessAction() {
      yield put(visitsActions.describeVisit({ orgId, visitId }));
      yield put(visitsActions.fetchVisits({ orgId }));
    },
  });
}

function* removeVisitorFromVisitSaga(
  action: ReturnType<typeof visitorsActions.removeVisitorFromVisit>,
): Generator {
  const { orgId, visitId, visitorId } = action.payload;
  yield requestAndSet('removeVisitors', [orgId, visitId, visitorId], {
    loadingAction: (v: boolean) =>
      visitorsActions.setLoading({ key: 'visitorLoading', value: v }),
    *postSuccessAction() {
      yield put(visitsActions.fetchVisits({ orgId }));
      yield put(visitsActions.describeVisit({ orgId, visitId }));
    },
  });
}

function* viewVisitorPhotoSaga(
  action: ReturnType<typeof visitorsActions.viewVisitorPhoto>,
): Generator {
  const { orgId, visitId, visitorId } = action.payload;
  try {
    yield put(
      visitorsActions.setLoading({ key: 'visitorLoading', value: true }),
    );
    const response = (yield call(
      heliumReceive,
      'getVisitPhoto',
      [orgId, visitId, visitorId],
      {
        axiosConfig: { responseType: 'blob' },
      },
    )) as BlobResponse;
    const imageUrl = URL.createObjectURL(response.data);
    yield put(visitorsActions.setVisitorPhotoUrl(imageUrl));
  } catch (e: unknown) {
    const message =
      typeof e === 'object' && e !== null && 'message' in e
        ? String((e as { message?: unknown }).message || '')
        : '';
    yield put(visitorsActions.setError(message));
  } finally {
    yield put(
      visitorsActions.setLoading({ key: 'visitorLoading', value: false }),
    );
  }
}

export default function* visitorsSaga(): Generator {
  yield all([
    takeLatest(visitorsActions.updateVisitor.type, updateVisitorSaga),
    takeLatest(visitorsActions.addVisitorsToVisit.type, addVisitorsToVisitSaga),
    takeLatest(
      visitorsActions.removeVisitorFromVisit.type,
      removeVisitorFromVisitSaga,
    ),
    takeLatest(visitorsActions.viewVisitorPhoto.type, viewVisitorPhotoSaga),
  ]);
}
